/**
 * Safe service worker registration utility.
 * Ensures only ONE service worker controls the app and
 * cleans up any conflicting registrations.
 */

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  // Check if running inside Median/Capacitor WebView
  const isMedianWebView = !!(window as any).median || !!(window as any).gonative;

  try {
    // Get all existing registrations and clean up duplicates
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const reg of registrations) {
      const swUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || '';

      // Remove any OneSignal or third-party service workers
      if (swUrl.includes('OneSignal') || swUrl.includes('onesignal')) {
        console.log('[SW] Removing conflicting OneSignal SW');
        await reg.unregister();
        continue;
      }

      // In Median WebView, let the native wrapper handle everything
      if (isMedianWebView && !swUrl.includes('sw-custom')) {
        console.log('[SW] Removing non-custom SW in Median context');
        await reg.unregister();
      }
    }

    // VitePWA handles the main SW registration via injectRegister: "script-defer"
    // We don't double-register here, just clean up conflicts.
  } catch (err) {
    console.warn('[SW] Registration cleanup failed:', err);
  }
}

/**
 * Force update: clear all caches and reload.
 * Useful for recovering from white-screen issues.
 */
export async function forceAppUpdate(): Promise<void> {
  try {
    // Clear all caches
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));

    // Unregister all service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));

    // Reload
    window.location.reload();
  } catch (err) {
    console.warn('[SW] Force update failed:', err);
    window.location.reload();
  }
}
